import { FC, useCallback } from 'react'
import {
  MdArrowOutward, MdCheck, MdInventory2, MdLocalShipping, MdOutlineQrCode2,
  MdPlace, MdWarningAmber, MdWhereToVote
} from 'react-icons/md'
import ApiStateView from '../ApiState'
import BackButton from '../BackButton'
import Panel from '../Panel'
import { suppliesApi, SupplyShipmentDetail, SupplyShipmentEstadoLocal } from '../../api'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { COURIER, TERMINO_ETIQUETA } from '../../constants'

// Paleta de marca Reuse
const FOUNDATION = '#151930'
const SOLID = '#37417f'
const RE_BLUE = '#4b75f7'
const LILAC = '#afb9ff'
const ALERT = '#f06464'

// Posición en el stepper de 3 hitos. "atrasada" se ubica en camino pero pinta en alerta.
const STEP_INDEX: Record<SupplyShipmentEstadoLocal, number> = { lista: 0, en_transito: 1, entregada: 2, atrasada: 1 }

const STEPS = [
  { key: 'creada', label: 'Creada', Icon: MdInventory2 },
  { key: 'en_camino', label: 'En camino', Icon: MdLocalShipping },
  { key: 'entregada', label: 'Entregada', Icon: MdWhereToVote }
]

const fmtFechaHora = (iso: string | null): { fecha: string, hora: string } => {
  if (iso === null || iso === '') return { fecha: '—', hora: '' }
  const d = new Date(iso)
  const dias = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
  const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
  const fecha = `${dias[d.getDay()]} ${d.getDate()} ${meses[d.getMonth()]}`
  const hora = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  return { fecha, hora }
}

/* ----------------------------- Stepper de 4 hitos ----------------------------- */
const ProgressStepper: FC<{ estado: SupplyShipmentEstadoLocal }> = ({ estado }) => {
  const current = STEP_INDEX[estado]
  const isAtrasada = estado === 'atrasada'
  const isDone = estado === 'entregada'

  return (
    <div className='flex items-start w-full'>
      {STEPS.map((step, i) => {
        const done = i < current || isDone
        const active = i === current && !isDone
        const bg = active ? (isAtrasada ? ALERT : FOUNDATION) : done ? SOLID : '#eceef6'
        const iconColor = active || done ? '#fff' : '#9aa1c0'
        const Icon = active && isAtrasada ? MdWarningAmber : step.Icon
        return (
          <div key={step.key} className='flex items-start flex-1 last:flex-none'>
            <div className='flex flex-col items-center'>
              <span className={`mb-2 text-[14px] text-center leading-tight ${active || done ? 'font-semibold text-[#151930]' : 'text-[#151930]/40'}`}>
                {step.label}
              </span>
              <div
                className='w-12 h-12 rounded-full flex items-center justify-center transition-colors'
                style={{ background: bg, boxShadow: active ? `0 0 0 5px ${(isAtrasada ? ALERT : RE_BLUE)}26` : undefined }}
              >
                {done ? <MdCheck className='text-xl' style={{ color: '#fff' }} /> : <Icon className='text-xl' style={{ color: iconColor }} />}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div className='flex-1 h-[3px] mx-1 mt-[34px] rounded-full' style={{ background: i < current || isDone ? SOLID : '#e3e6f0' }} />
            )}
          </div>
        )
      })}
    </div>
  )
}

const TrackingInsumosView: FC = () => {
  const idSeller = useSelectedSeller()
  const { selectedTracking } = useLogisticBox()
  const fetchDetail = useCallback(async (): Promise<SupplyShipmentDetail | null> => {
    if (selectedTracking === null) throw new Error('Selecciona un envío desde Solicitudes y envíos')
    return await suppliesApi.shipmentDetail(selectedTracking, idSeller)
  }, [selectedTracking, idSeller])

  const detail = useApi<SupplyShipmentDetail | null>(fetchDetail, [selectedTracking, idSeller])

  const d = detail.data
  const estado = d?.estado_local ?? 'lista'
  const pdfUrl = d?.pdf_url ?? null
  const eventos = d?.eventos ?? []

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between flex-wrap gap-3'>
        <BackButton to='insumos' label='Volver a Insumos' />
      </div>

      <ApiStateView
        loading={detail.loading}
        error={detail.error}
        onRetry={detail.reload}
        empty={!detail.loading && d === null}
        emptyText='No se encontró tracking para este envío'
      >
        {d !== null && (
          <div className='space-y-4'>
            {/* ───── Card principal estilo tracking público ───── */}
            <Panel className='!p-0 overflow-hidden'>
              {/* Hero oscuro */}
              <div
                className='relative px-6 py-6 overflow-hidden'
                style={{ background: `linear-gradient(120deg, ${FOUNDATION} 0%, ${SOLID} 100%)` }}
              >
                <div className='absolute -right-10 -top-16 w-48 h-48 rounded-full' style={{ border: `2px solid ${LILAC}33` }} />
                <div className='absolute -right-2 -bottom-20 w-40 h-40 rounded-full' style={{ border: `2px solid ${LILAC}22` }} />

                <div className='relative flex items-start justify-between flex-wrap gap-3'>
                  <div>
                    <div className=' text-2xl font-bold text-white tracking-wide'>
                      {d.tracking_number ?? 'Sin tracking'}
                    </div>
                    <div className='text-xs text-white/55 mt-1'>
                      Envío de insumos N° <span className='font-semibold text-white/80'>{d.id_shipment}</span>
                      <span className='mx-1.5'>·</span>{d.store}
                    </div>
                  </div>
                  <span
                    className='text-[14px] font-bold uppercase tracking-wider text-white px-3 py-1.5 rounded-full'
                    style={{ background: RE_BLUE }}
                  >
                    {COURIER}
                  </span>
                </div>
              </div>

              {/* Cuerpo claro */}
              <div className='p-6 space-y-6'>
                {/* Origen / Destino */}
                <div className='rounded-2xl bg-[#f3f4f8] p-4'>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                    <div>
                      <div className='text-xs text-[#151930]/55'>Origen</div>
                      <div className='text-sm font-semibold text-[#151930] mt-0.5'>{d.origen}</div>
                    </div>
                    <div className='sm:border-l sm:border-[#dfe2ee] sm:pl-4'>
                      <div className='text-xs text-[#151930]/55'>Destino</div>
                      <div className='text-sm font-semibold text-[#151930] mt-0.5'>{d.destino}</div>
                    </div>
                  </div>
                </div>

                {/* Stepper */}
                <ProgressStepper estado={estado} />

                {/* Meta inferior */}
                <div className='flex items-center justify-between flex-wrap gap-3 pt-1 border-t border-[#eef0f7] text-xs text-[#151930]/55'>
                  <div className='flex items-center gap-4 flex-wrap'>
                    <span><span className='text-[#151930]/40'>Contenido:</span> <span className='font-medium text-[#151930]'>{d.contenido_resumen ?? '—'}</span></span>
                    <span><span className='text-[#151930]/40'>ETA:</span> <span className='font-semibold text-[#151930]'>{d.eta_entrega !== null ? fmtFechaHora(d.eta_entrega).fecha : '—'}</span></span>
                  </div>
                  <span className='text-[#151930]/40'>Despacho {fmtFechaHora(d.fecha_despacho).fecha}</span>
                </div>
              </div>
            </Panel>

            {/* ───── Timeline + acciones ───── */}
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
              {/* Historial */}
              <div className='lg:col-span-2'>
                <Panel>
                  <div className='flex items-center justify-between mb-4'>
                    <h3 className='font-semibold text-[#151930] text-sm'>Historial de eventos</h3>
                    <span className='text-[14px] text-[#151930]/45'>{eventos.length} eventos · {COURIER}</span>
                  </div>

                  {eventos.length === 0
                    ? (
                      <div className='flex flex-col items-center justify-center py-10 text-center'>
                        <div className='w-12 h-12 rounded-full bg-[#f3f4fb] flex items-center justify-center mb-3'>
                          <MdLocalShipping className='text-2xl text-[#151930]/30' />
                        </div>
                        <p className='text-sm text-[#151930]/60'>Aún no hay eventos sincronizados</p>
                      </div>
                      )
                    : (
                      <ol className='relative'>
                        {eventos.map((ev, i) => {
                          const isLast = i === eventos.length - 1
                          const { fecha, hora } = fmtFechaHora(ev.fecha_evento)
                          return (
                            <li key={ev.id ?? i} className='relative pl-7 pb-5 last:pb-0'>
                              <span className='absolute left-0 top-0.5 w-4 h-4 rounded-full flex items-center justify-center' style={{ background: isLast ? `${RE_BLUE}22` : '#eef0f8' }}>
                                <span className='w-2 h-2 rounded-full' style={{ background: isLast ? RE_BLUE : SOLID }} />
                              </span>
                              {!isLast && <span className='absolute left-[7px] top-5 bottom-0 w-[2px] bg-[#eef0f8]' />}
                              <div className='flex items-start justify-between gap-3'>
                                <div>
                                  <div className='text-sm font-medium text-[#151930]'>{ev.evento_codigo ?? ev.descripcion ?? 'Evento'}</div>
                                  {ev.descripcion !== null && ev.evento_codigo !== null && (
                                    <div className='text-xs text-[#151930]/55 mt-0.5'>{ev.descripcion}</div>
                                  )}
                                  {ev.ubicacion !== null && (
                                    <div className='flex items-center gap-1 text-[14px] text-[#151930]/45 mt-0.5'>
                                      <MdPlace className='text-xs' />{ev.ubicacion}
                                    </div>
                                  )}
                                </div>
                                <div className='text-right shrink-0'>
                                  <div className='text-xs font-medium text-[#151930]'>{fecha}</div>
                                  <div className='text-[14px] text-[#151930]/45'>{hora}</div>
                                </div>
                              </div>
                            </li>
                          )
                        })}
                      </ol>
                      )}
                </Panel>
              </div>

              {/* Acciones */}
              <div className='space-y-4'>
                {pdfUrl !== null && (
                  <Panel>
                    <h3 className='font-semibold text-[#151930] text-sm mb-3'>{TERMINO_ETIQUETA}</h3>
                    <a
                      href={pdfUrl} target='_blank' rel='noreferrer'
                      className='flex items-center justify-between rounded-xl border border-[#eef0f7] hover:border-[#c9d0ea] px-3 py-3 transition-colors group'
                    >
                      <div className='flex items-center gap-2'>
                        <div className='w-9 h-9 rounded-lg bg-[#f3f4fb] flex items-center justify-center'>
                          <MdOutlineQrCode2 className='text-lg' style={{ color: SOLID }} />
                        </div>
                        <div>
                          <div className='text-sm font-medium text-[#151930]'>{TERMINO_ETIQUETA} {COURIER}</div>
                          <div className='text-[14px] text-[#151930]/50'>Abrir documento</div>
                        </div>
                      </div>
                      <MdArrowOutward className='text-[#151930]/40 group-hover:text-[#37417f]' />
                    </a>
                  </Panel>
                )}
              </div>
            </div>
          </div>
        )}
      </ApiStateView>
    </div>
  )
}

export default TrackingInsumosView
